import { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { formatDuration, formatTimeStamp } from "../../api/callData";
import { useCallData } from "../../contexts/CallDataContext";
import { useWeek } from "../../contexts/WeekContext";
import { addDays, isWithinInterval, startOfDay, endOfDay } from "date-fns";
import { parseCustomTimestamp } from "../../utils/dateUtils";
import { getOrderStatus, getEquipmentList } from "../../utils/bookingUtils";

import { Icon } from "@iconify/react/dist/iconify.js";

const getBookingStatusStyles = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower?.includes('confirmed') || statusLower?.includes('completed')) {
        return { textColor: 'text-green-500', icon: 'material-symbols:check-circle-outline' };
    }
    if (statusLower?.includes('pending') || statusLower?.includes('processing')) {
        return { textColor: 'text-yellow-500', icon: 'material-symbols:pending-actions-outline' };
    }
    if (statusLower?.includes('cancelled') || statusLower?.includes('rejected')) {
        return { textColor: 'text-error', icon: 'material-symbols:cancel-outline' };
    }
    return { textColor: 'text-font-secondary', icon: 'ph:question' };
};

// const getTrendStyles = (trend) => {
//     const styles = {
//         improving: {
//             textColor: "text-green-500",
//             icon: "iconamoon:trend-up-light"
//         },
//         declining: {
//             textColor: "text-error",
//             icon: "iconamoon:trend-down-light"
//         },
//         stable: {
//             textColor: "text-secondary",
//             icon: "mdi:trending-neutral"
//         },
//         neutral: {
//             textColor: "text-font-secondary",
//             icon: "ph:empty"
//         }
//     };

//     return styles[trend] || styles.neutral;
// };

const ChatTable = ({ onRowClick, isShort, chatRow, displayData, selectedRows, setSelectedRows }) => {
    const { dateRange } = useWeek();

    const [copiedId, setCopiedId] = useState(null);
    const [sortedData, setSortedData] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10); // Default, will be recalculated
    const tableBodyRef = useRef(null); // Ref for the table body container
    const rowRef = useRef(null); // Ref for a single row to measure its height
    const cardRef = useRef(null); // Ref for a single card to measure its height

    const [isCalculationComplete, setIsCalculationComplete] = useState(false);

    const { data } = useCallData();

    useEffect(() => {
        setSortedData(displayData || []);
    }, [displayData]);

    useLayoutEffect(() => {
        const calculateRows = () => {
            if (!tableBodyRef.current) return;

            const containerHeight = tableBodyRef.current.offsetHeight;
            let itemHeight = 0;

            // Determine which view is active and get the appropriate element height
            const isDesktopView = window.innerWidth >= 1280; // xl breakpoint

            if (isDesktopView && rowRef.current) {
                // Desktop table view - use table row height
                itemHeight = rowRef.current.offsetHeight;
            } else if (!isDesktopView && cardRef.current) {
                // Mobile card view - use card height
                itemHeight = cardRef.current.offsetHeight;
            } else if (rowRef.current) {
                // Fallback to rowRef if cardRef not available
                itemHeight = rowRef.current.offsetHeight;
            }

            if (itemHeight > 0 && containerHeight > 0) {
                // Calculate rows based on available height
                const calculatedRows = Math.floor(containerHeight / itemHeight);
                const newRowsPerPage = Math.max(1, calculatedRows);

                // Only update if the value actually changed to prevent infinite loops
                if (newRowsPerPage !== rowsPerPage) {
                    setRowsPerPage(newRowsPerPage);
                    setCurrentPage(1); // Reset to first page when rows per page changes
                }
                setIsCalculationComplete(true);
            }
        };

        // Delay initial calculation to ensure DOM is ready
        const timeoutId = setTimeout(calculateRows, 100);

        // ResizeObserver for container size changes
        const resizeObserver = new ResizeObserver(() => {
            setIsCalculationComplete(false);
            setTimeout(calculateRows, 50);
        });

        if (tableBodyRef.current) {
            resizeObserver.observe(tableBodyRef.current);
        }

        // Window resize listener for screen size changes
        const handleWindowResize = () => {
            setIsCalculationComplete(false);
            setTimeout(calculateRows, 100);
        };

        window.addEventListener('resize', handleWindowResize);

        return () => {
            clearTimeout(timeoutId);
            resizeObserver.disconnect();
            window.removeEventListener('resize', handleWindowResize);
        };
    }, [sortedData, rowsPerPage]); // Include isShort in dependencies to recalculate when ChatDetails opens/closes

    // Additional effect to handle responsive breakpoint changes
    useEffect(() => {
        const handleBreakpointChange = () => {
            setIsCalculationComplete(false);
            setRowsPerPage(10); // Reset to default
            setTimeout(() => {
                if (!tableBodyRef.current) return;

                const containerHeight = tableBodyRef.current.offsetHeight;
                let itemHeight = 0;

                // Determine which view is active after breakpoint change
                const isDesktopView = window.innerWidth >= 1280;

                if (isDesktopView && rowRef.current) {
                    itemHeight = rowRef.current.offsetHeight;
                } else if (!isDesktopView && cardRef.current) {
                    itemHeight = cardRef.current.offsetHeight;
                } else if (rowRef.current) {
                    itemHeight = rowRef.current.offsetHeight;
                }

                if (itemHeight > 0 && containerHeight > 0) {
                    const calculatedRows = Math.floor(containerHeight / itemHeight);
                    const newRowsPerPage = Math.max(1, calculatedRows);
                    
                    setRowsPerPage(newRowsPerPage);
                    setCurrentPage(1);
                    setIsCalculationComplete(true);
                }
            }, 200);
        };

        // Listen for media query changes at xl breakpoint (1280px)
        const mediaQuery = window.matchMedia('(min-width: 1280px)');
        mediaQuery.addEventListener('change', handleBreakpointChange);

        return () => {
            mediaQuery.removeEventListener('change', handleBreakpointChange);
        };
    }, [isShort]); // Add isShort as dependency to recalculate when ChatDetails opens/closes

    const selectAll = (e) => {
        if(e.target.checked) {
            setSelectedRows(data.map((chat) => chat.conversation_id));
        } else {
            setSelectedRows([]);
        }
    };

    const selectRow = (id) => {
        setSelectedRows((prev) => {
            return prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        });
    };

    // --- DATA SLICING FOR PAGINATION ---
    const totalPages = rowsPerPage > 0 ? Math.ceil(sortedData.length / rowsPerPage) : 1;
    const startIndex = (currentPage - 1) * rowsPerPage;
    const paginatedData = isCalculationComplete
        ? sortedData.slice(startIndex, startIndex + rowsPerPage)
        : sortedData.slice(0, 1);

    return (
        <div
            className={
                `w-full h-full flex flex-col`
            }
        >
            {/* Desktop Table Header (xl and above) */}
            <div className="flex-shrink-0 hidden md:block">
                <table className="w-full table-auto text-left border-collapse text-sm">
                    <thead className=" ">
                        <tr className={`
                            text-font-primary h-[48px] font-semibold bg-bgSecondary border border-border rounded-md`}
                        >
                            <th className="pl-4 w-[16px]">
                                <input
                                    type="checkbox"
                                    onChange={selectAll}
                                    checked={selectedRows.length === data.length && data.length > 0}
                                    className="w-4 h-4 flex-shrink-0 cursor-pointer"
                                />
                            </th>
                            {!isShort && (
                                <th className="w-[100px] truncate max-w-[100px] pr-2">Session ID</th>
                            )}
                            <th className="w-[30px] truncate max-w-[30px] pr-2">Messages</th>
                            <th className="w-[70px] truncate max-w-[70px] pr-2">Timestamp</th>
                            <th className="w-[70px] truncate max-w-[70px] px-2">Sentiment</th>
                            <th className="w-[70px] max-w-[70px] px-2 whitespace-nowrap truncate">Success Evaluation</th>
                        </tr>
                    </thead>
                </table>
            </div>

            {/* Mobile Header (below xl) */}
            <div className="flex-shrink-0 md:hidden">
                <div className={`
                    flex items-center justify-between px-3 py-2 text-font-secondary text-sm h-[48px] font-semibold 
                    ${!isShort ? 'bg-bgSecondary border border-border' : 'bg-bgSecondary border border-border'}`}
                >
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            onChange={selectAll}
                            checked={selectedRows.length === data.length && data.length > 0}
                            className="w-4 h-4 flex-shrink-0 cursor-pointer"
                        />
                        <span className="font-semibold text-font-primary">Select All</span>
                    </div>
                    <span className="font-normal text-xs">
                        {selectedRows.length} selected
                    </span>
                </div>
            </div>
            <div ref={tableBodyRef} className="flex-1 overflow-y-auto scrollable min-h-0">
                {/* Desktop Table View (xl and above) */}
                <table className="w-full table-auto text-left border-collapse text-sm hidden md:table">
                    <tbody className="">
                        {paginatedData
                        .filter(chat => chat.conversation_id !== null)
                        .map((chat, index) => {
                            // Assign ref to the first row for height measurement
                            const ref = index === 0 ? rowRef : null;
                            return (
                                <tr key={chat.conversation_id}
                                    ref={ref}
                                    className={
                                        `h-[56px] text-font-secondary hover:text-primary hover:bg-background hover:shadow-neo cursor-pointer transition-colors ease-out-in duration-300
                                        ${chatRow === chat.conversation_id ? 'bg-bgSecondary shadow-neo-card' : ''}`
                                    }
                                    onClick={() => onRowClick && onRowClick(chat)}
                                >
                                    <td className="pl-4 w-[16px]">
                                        <input
                                        type="checkbox"
                                        className={`w-4 h-4 flex-shrink-0 cursor-pointer ${chatRow === chat.conversation_id ? '' : ''}`}
                                        checked={selectedRows.includes(chat.conversation_id)}
                                        onClick={(e) => e.stopPropagation()} // Prevent row click
                                        onChange={() => selectRow(chat.conversation_id)}
                                        />
                                    </td>
                                    {!isShort && (
                                        <td className="w-[100px] truncate max-w-[100px] pr-2 text-font-secondary font-semibold">{chat.metadata.session_id || 'N/A'}</td>
                                    )}
                                    <td className="w-[30px] truncate max-w-[30px] pr-2 font-medium">{chat.analytics.message_count || 'N/A'}</td>
                                    <td className="w-[70px] truncate max-w-[70px] pr-2 font-medium">{formatTimeStamp(chat?.metadata?.timestamp)}</td>
                                    <td className="w-[70px] truncate max-w-[70px] p-2 capitalize">
                                        <div className={`w-fit h-fit p-2 text-xs flex items-center gap-1 justify-center rounded-md font-semibold
                                            ${chatRow === chat.conversation_id ? 'bg-bgSecondary border-border h-[36px]' : 'bg-bgSecondary border border-border'}
                                            ${chat.analytics.user_sentiment === 'positive' ? 'text-green-500 bg-green-500/10' : chat.analytics.user_sentiment === 'negative' ? 'text-error bg-error/10' : 'text-font-secondary bg-font-secondary/10'}`}>
                                            <Icon icon={chat.analytics.user_sentiment === 'positive' ? 'iconamoon:trend-up-light' : chat.analytics.user_sentiment === 'negative' ? 'iconamoon:trend-down-light' : 'ph:empty'} className="text-base" />
                                            {chat.analytics.user_sentiment || 'neutral'}
                                        </div>
                                    </td>
                                    <td className="w-[70px] truncate max-w-[70px] p-2">
                                        <div className={`
                                            w-fit h-fit p-2 text-xs rounded-md flex items-center gap-1 justify-center font-semibold
                                            ${chat.evaluation.success_score >= 7 ? 'text-green-500 bg-green-500/10' : chat.evaluation.success_score >= 4 ? 'text-yellow-500 bg-yellow-500/10' : 'text-error bg-error/10'}
                                            ${chatRow === chat.conversation_id ? 'bg-bgSecondary border-border h-[36px]' : 'bg-bgSecondary border border-border'}
                                        `}>
                                            <Icon icon={chat.evaluation.success_score >= 7 ? 'fa-regular:smile' : chat.evaluation.success_score >= 4 ? 'fontisto:neutral' : 'bx:sad'} className="text-base" />
                                            {chat.evaluation.success_score >= 7 ? 'Pass' : chat.evaluation.success_score >= 4 ? 'Partial' : 'Fail'}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                {/* Mobile Card View (below xl) */}
                <div className="md:hidden space-y-2 p-2">
                    {paginatedData
                    .filter(chat => chat.conversation_id !== null)
                    .map((chat, index) => {
                        // Assign ref to the first card for height measurement
                        const ref = index === 0 ? cardRef : null;
                        return (
                            <div
                                key={chat.conversation_id}
                                ref={ref}
                                className={`
                                    p-3 rounded-lg flex flex-col gap-4 cursor-pointer transition-all duration-300 ease-out-in
                                    hover:shadow-lg hover:scale-[1.02]
                                    ${chatRow === chat.conversation_id ? '' : 'card-container bg-background'}
                                    ${chatRow === chat.conversation_id ? 'border-2 border-primary' : 'border border-border'}
                                `}
                                onClick={() => onRowClick && onRowClick(chat)}
                            >
                                <div className="flex items-start justify-between ">
                                    <div className="flex flex-row w-full justify-between items-center gap-2">
                                        <div className="flex flex-row items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={selectedRows.includes(chat.conversation_id)}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={() => selectRow(chat.conversation_id)}
                                                className="w-4 h-4 flex-shrink-0 cursor-pointer"
                                            />
                                            <span className="font-semibold text-font-primary text-sm">
                                                {chat.metadata.session_id}
                                            </span>
                                        </div>
                                        <span className="text-sm text-font-secondary">
                                            {formatTimeStamp(chat?.metadata?.timestamp)}
                                        </span>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <div className={`w-fit h-fit p-1.5 text-xs rounded flex items-center gap-1
                                            ${chat.analytics.user_sentiment === 'positive' ? 'text-green-500' : chat.analytics.user_sentiment === 'negative' ? 'text-error' : 'text-font-secondary'}
                                            ${chatRow === chat.conversation_id ? '' : ''}`}>
                                            <Icon icon={chat.analytics.user_sentiment === 'positive' ? 'iconamoon:trend-up-light' : chat.analytics.user_sentiment === 'negative' ? 'iconamoon:trend-down-light' : 'ph:empty'} className="text-sm" />
                                            <span className="capitalize text-font-primary">{chat.analytics.user_sentiment || 'neutral'}</span>
                                        </div>
                                        <div className={`w-fit h-fit p-1.5 text-xs rounded flex items-center gap-1
                                            ${chat.evaluation.success_score >= 7 ? 'tag-green text-green-500' : chat.evaluation.success_score >= 4 ? 'tag-orange text-orange-500' : 'tag-red text-red-500'}
                                            ${chatRow === chat.conversation_id ? '' : ''}`}>
                                            <Icon icon={chat.evaluation.success_score >= 7 ? 'fa-regular:smile' : chat.evaluation.success_score >= 4 ? 'fontisto:neutral' : 'bx:sad'} className="text-sm" />
                                            <span className="text-font-primary">{chat.evaluation.success_score >= 7 ? 'Pass' : chat.evaluation.success_score >= 4 ? 'Partial' : 'Fail'}</span>
                                        </div>
                                        <div className={`w-fit h-fit p-1.5 text-xs rounded flex items-center gap-1 text-font-secondary tag-gray ${chatRow === chat.conversation_id ? '' : ''}`}>
                                            <Icon icon="ph:question" className="text-sm" />
                                            <span className="text-font-secondary">{chat.analytics.resolution_status || 'unknown'}</span>
                                        </div>
                                    </div>
                                    <span className="text-sm text-font-secondary">
                                        {chat.analytics?.message_count || 0} messages
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {paginatedData.length === 0 && (
                    <div className="text-center py-10 text-gray-400">No chats found.</div>
                )}
            </div>

            <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-t border-bgAccent text-sm">
                <div className="hidden sm:block">
                    <span className="text-font-secondary">
                        Showing <span className="font-semibold text-primary">{Math.min(startIndex + 1, sortedData.length)}</span> to <span className="font-semibold text-primary">{Math.min(startIndex + rowsPerPage, sortedData.length)}</span> of <span className="font-semibold text-primary">{sortedData.length}</span> results
                    </span>
                </div>
                <div className="flex items-center gap-1 sm:gap-2 px-1 sm:px-4 w-full sm:w-auto justify-between sm:justify-end">
                     <button
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="p-1 sm:p-2 disabled:opacity-50 disabled:cursor-not-allowed button-container"
                    >
                        <Icon icon="icon-park-outline:double-left" className="text-lg sm:text-xl text-accent" />
                    </button>
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="p-1 sm:p-2 disabled:opacity-50 disabled:cursor-not-allowed button-container"
                    >
                        <Icon icon="material-symbols:arrow-back-ios-new" className="text-base sm:text-lg text-accent" />
                    </button>
                    <span className="text-font-secondary text-xs sm:text-sm">
                        <span className="font-bold text-accent">{currentPage}</span>/<span className="font-bold">{totalPages}</span>
                    </span>
                    <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="p-1 sm:p-2 disabled:opacity-50 disabled:cursor-not-allowed button-container"
                    >
                        <Icon icon="material-symbols:arrow-forward-ios" className="text-base sm:text-lg text-accent" />
                    </button>
                    <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        className="p-1 sm:p-2 disabled:opacity-50 disabled:cursor-not-allowed button-container"
                    >
                        <Icon icon="icon-park-outline:double-right" className="text-lg sm:text-xl text-accent" />
                    </button>
                </div>
            </div>
        </div>
    )
};

export default ChatTable;
